import type { SpotShape, ZoneShape } from "@gettactic/map-builder/src/types";
import { normalizeZonePolygon } from "@gettactic/map-builder/src/utils/zonePolygon";
import { updateShape } from "../document/operations";
import { isPrimaryButton } from "../gesture/pointer";
import type { Point } from "../types";
import type { Tool, ToolContext } from "./Tool";

const CLOSE_THRESHOLD_PX = 15;

interface PolygonTarget {
  /** Current corners, in the target's local frame. */
  points: (target: ZoneShape | SpotShape) => Point[];
  /** World point → target-local point. */
  toLocal: (target: ZoneShape | SpotShape, world: Point) => Point;
  /** Commits one more corner. */
  append: (
    target: ZoneShape | SpotShape,
    point: Point
  ) => Partial<ZoneShape | SpotShape>;
  /** Finalises (or reverts a too-short outline) when drawing stops. */
  finish: (target: ZoneShape | SpotShape) => Partial<ZoneShape | SpotShape>;
  isTarget: (
    shape: ZoneShape | SpotShape | undefined
  ) => shape is ZoneShape | SpotShape;
}

const zoneTarget: PolygonTarget = {
  points: (target) => (target as ZoneShape).vertices ?? [],
  toLocal: (target, world) => ({
    x: Math.round(world.x - target.x),
    y: Math.round(world.y - target.y)
  }),
  append: (target, point) => {
    const vertices = [...((target as ZoneShape).vertices ?? []), point];
    return { vertices, edges: vertices.map(() => ({})) } as Partial<ZoneShape>;
  },
  finish: (target) => {
    const zone = target as ZoneShape;
    const vertices = zone.vertices ?? [];
    if (vertices.length < 3) {
      return { vertices: undefined, edges: undefined } as Partial<ZoneShape>;
    }
    return normalizeZonePolygon(
      zone,
      vertices,
      vertices.map(() => ({}))
    );
  },
  isTarget: (shape): shape is ZoneShape => shape?.type === "zone"
};

const stateAreaTarget: PolygonTarget = {
  points: (target) => {
    const area = (target as SpotShape).stateArea;
    return area?.kind === "polygon" ? (area.points ?? []) : [];
  },
  toLocal: (target, world) => {
    // Into the spot's own frame: undo its rotation about its origin.
    const radians = ((target.rotation || 0) * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const dx = world.x - target.x;
    const dy = world.y - target.y;
    return { x: dx * cos + dy * sin, y: -dx * sin + dy * cos };
  },
  append: (target, point) => {
    const spot = target as SpotShape;
    const current =
      spot.stateArea?.kind === "polygon" ? (spot.stateArea.points ?? []) : [];
    return {
      stateArea: {
        kind: "polygon",
        hideDot: spot.stateArea?.hideDot,
        points: [...current, point]
      }
    } as Partial<SpotShape>;
  },
  finish: (target) => {
    const spot = target as SpotShape;
    if (
      spot.stateArea?.kind === "polygon" &&
      (spot.stateArea.points?.length ?? 0) < 3
    ) {
      return { stateArea: undefined } as Partial<SpotShape>;
    }
    return {};
  },
  isTarget: (shape): shape is SpotShape => shape?.type === "spot"
};

/**
 * Corner-by-corner drawing onto the selected zone (its polygon outline) or
 * spot (its availability-state area). Each click commits straight to the
 * shape so it renders live; double-click, right-click, Enter, Escape or
 * clicking the first corner finishes and returns to the select tool.
 */
const createPolygonTool = (
  kind: "zone-polygon" | "state-area",
  target: PolygonTarget
): Tool => {
  let targetId: string | null = null;

  const currentTarget = (toolContext: ToolContext) => {
    const state = toolContext.store.getState();
    const id = targetId ?? state.selection[0] ?? null;
    const shape = id
      ? (state.shapes[id] as ZoneShape | SpotShape | undefined)
      : undefined;
    return target.isTarget(shape) ? shape : null;
  };

  const finish = (toolContext: ToolContext) => {
    const shape = targetId
      ? toolContext.store.getState().shapes[targetId]
      : undefined;
    if (shape && target.isTarget(shape as ZoneShape | SpotShape)) {
      const patch = target.finish(shape as ZoneShape | SpotShape);
      if (Object.keys(patch).length > 0) {
        toolContext.store.getState().updateShape(shape.id, patch);
      }
    }
    targetId = null;
    toolContext.store.getState().setTool("select");
  };

  return {
    kind,
    cursor: "crosshair",
    isBusy: () => targetId !== null,

    onPointerDown(toolContext, event) {
      const shape = currentTarget(toolContext);
      if (!shape) {
        targetId = null;
        toolContext.store.getState().setTool("select");
        return;
      }
      targetId = shape.id;
      if (event.button === 2) {
        finish(toolContext);
        return;
      }
      if (!isPrimaryButton(event)) return;

      const world = toolContext.snap.snapPoint(
        toolContext.toWorld({ x: event.clientX, y: event.clientY })
      );
      const local = target.toLocal(shape, world);
      const points = target.points(shape);
      if (points.length >= 2) {
        const first = points[0];
        const threshold =
          CLOSE_THRESHOLD_PX /
          Math.max(toolContext.store.getState().viewport.scale, 0.1);
        if (Math.hypot(local.x - first.x, local.y - first.y) < threshold) {
          finish(toolContext);
          return;
        }
      }
      toolContext.store
        .getState()
        .commit((document) =>
          updateShape(document, shape.id, target.append(shape, local))
        );
    },

    onPointerMove() {},
    onPointerUp() {},

    onDoubleClick(toolContext) {
      if (targetId) finish(toolContext);
    },

    onKeyDown(toolContext, event) {
      if ((event.key === "Escape" || event.key === "Enter") && targetId) {
        finish(toolContext);
        return true;
      }
      return false;
    },

    cancel(toolContext) {
      // Leaving the tool any other way keeps a finished outline and drops an
      // unfinished one, without forcing the select tool.
      const shape = targetId
        ? toolContext.store.getState().shapes[targetId]
        : undefined;
      if (shape && target.isTarget(shape as ZoneShape | SpotShape)) {
        const patch = target.finish(shape as ZoneShape | SpotShape);
        if (Object.keys(patch).length > 0) {
          toolContext.store.getState().updateShape(shape.id, patch);
        }
      }
      targetId = null;
    }
  };
};

export const createZonePolygonTool = (): Tool =>
  createPolygonTool("zone-polygon", zoneTarget);

export const createStateAreaTool = (): Tool =>
  createPolygonTool("state-area", stateAreaTarget);
